import {
  Button,
  DetailsCard,
  Loading,
  PageHeader,
  Pagination,
  Tab,
  TabsPanel,
  TextRegular,
  icon as mdiIconPaths
} from '@hm/fundamental'
import {useRef, useState} from 'react'

import {
  DocumentLinksSection,
  ReportSelector,
  RiaFilters,
  RiaSummaryRow,
  RiaTable
} from '../components'
import type {SortDirection} from '../helpers'
import {useRiskSurveyTranslation} from '../hooks'
import type {
  ReportSummaryDto,
  ReportLinkDto,
  RiaListItemDto,
  RiaListResponse,
  RiaPriority,
  RiaStatus
} from '../types'

enum PlantPageTab {
  RiaTable = 'riaTable',
  DocumentLinks = 'documentLinks'
}

type RiskSurveyPlantPageProps = {
  siteName: string
  reports: ReportSummaryDto[]
  selectedReportId: string
  onReportChange: (reportId: string) => void
  riaData: RiaListResponse | undefined
  isLoading: boolean
  isError: boolean
  selectedRiaId?: string
  onRiaSelect: (ria: RiaListItemDto) => void
  onBack: () => void
  onImport: () => void
  onExport: () => void
  canManage: boolean
  statusFilter: RiaStatus[]
  onStatusFilterChange: (values: RiaStatus[]) => void
  priorityFilter: RiaPriority[]
  onPriorityFilterChange: (values: RiaPriority[]) => void
  onPageChange: (page: number, pageSize: number) => void
  onSortChange: (sortBy: string, sortDir: SortDirection) => void
  reportLinks: ReportLinkDto[]
  canManageLinks: boolean
  isAddingLink: boolean
  isDeletingLink: boolean
  onAddLink: (data: {url: string; label: string}) => void
  onDeleteLink: (linkId: string) => void
  detailPanel?: React.ReactNode
}

export const RiskSurveyPlantPage = ({
  siteName,
  reports,
  selectedReportId,
  onReportChange,
  riaData,
  isLoading,
  isError,
  selectedRiaId,
  onRiaSelect,
  onBack,
  onImport,
  onExport,
  canManage,
  statusFilter,
  onStatusFilterChange,
  priorityFilter,
  onPriorityFilterChange,
  onPageChange,
  onSortChange,
  reportLinks,
  canManageLinks,
  isAddingLink,
  isDeletingLink,
  onAddLink,
  onDeleteLink,
  detailPanel
}: RiskSurveyPlantPageProps) => {
  const {t} = useRiskSurveyTranslation()
  const tableContainerRef = useRef<HTMLDivElement>(null)
  const [selectedTab, setSelectedTab] = useState<PlantPageTab>(PlantPageTab.RiaTable)
  const hasActiveFilter = statusFilter.length > 0 || priorityFilter.length > 0

  return (
    <div className="flex flex-col gap-6 px-6 py-6 w-full max-w-full min-w-0 box-border overflow-x-hidden">
      <PageHeader
        backToPageLabel={t('common.back')}
        backToPageUrlOnClick={onBack}
        aside={
          <div className="flex items-center justify-end gap-2 flex-wrap max-w-full">
            {canManage && (
              <Button variant="secondary" onClick={onImport} data-cy="import-button">
                {t('plant.import')}
              </Button>
            )}
            <Button variant="secondary" onClick={onExport} data-cy="export-button">
              {t('plant.export')}
            </Button>
          </div>
        }
      >
        <span className="text-foreground_inverted">{siteName}</span>
      </PageHeader>
      <div className="flex flex-col gap-6 w-full max-w-full min-w-0 box-border">
        <DetailsCard className="p-4 max-w-full min-w-0 box-border">
          <div className="flex gap-4 flex-wrap items-end">
            {reports.length > 1 && (
              <div className="min-w-[280px] max-w-[420px] flex-[1_1_320px]">
                <ReportSelector
                  reports={reports}
                  selectedReportId={selectedReportId}
                  onReportChange={onReportChange}
                />
              </div>
            )}
            <div className="min-w-[320px] flex-[2_1_420px]">
              <RiaFilters
                statusFilter={statusFilter}
                onStatusFilterChange={onStatusFilterChange}
                priorityFilter={priorityFilter}
                onPriorityFilterChange={onPriorityFilterChange}
              />
            </div>
          </div>
        </DetailsCard>

        <div className="flex flex-col gap-0 w-full max-w-full min-w-0">
          <TabsPanel
            className="rounded-lg border border-foreground_faded_20 bg-container_background"
            onSelect={(value) => setSelectedTab(value as PlantPageTab)}
          >
            <Tab
              icon={mdiIconPaths.mdiTable}
              label={t('plant.riaTableTab')}
              value={PlantPageTab.RiaTable}
            />
            <Tab
              icon={mdiIconPaths.mdiFileLink}
              label={t('plant.documentLinksTab')}
              value={PlantPageTab.DocumentLinks}
            />
          </TabsPanel>

          {selectedTab === PlantPageTab.RiaTable ? (
            <div className="flex gap-4 w-full max-w-full min-w-0">
              <DetailsCard className="flex-1 min-w-0 max-w-full overflow-hidden box-border !rounded-t-none !border-t-0">
                {isLoading ? (
                  <Loading variant="primary" size="large" />
                ) : isError ? (
                  <TextRegular className="p-4">{t('common.error')}</TextRegular>
                ) : (
                  <>
                    <div
                      ref={tableContainerRef}
                      className="w-full max-w-full min-w-0 overflow-x-auto overflow-y-hidden"
                    >
                      <RiaTable
                        rias={riaData?.items ?? []}
                        selectedRiaId={selectedRiaId}
                        onRiaSelect={onRiaSelect}
                        onSortChange={onSortChange}
                      />
                      {riaData?.totals && (
                        <RiaSummaryRow
                          totalLePrior={riaData.totals.totalLePrior}
                          totalLeAfter={riaData.totals.totalLeAfter}
                          totalLeDelta={riaData.totals.totalLeDelta}
                          isFiltered={hasActiveFilter}
                          tableContainerRef={tableContainerRef}
                        />
                      )}
                    </div>
                    {riaData && riaData.totalPages > 1 && (
                      <div className="mt-4 p-4">
                        <Pagination
                          label={t('common.page')}
                          totalItems={riaData.totalCount}
                          defaultPage={riaData.page}
                          defaultPageSize={riaData.pageSize}
                          onChange={onPageChange}
                        />
                      </div>
                    )}
                  </>
                )}
              </DetailsCard>
              {detailPanel}
            </div>
          ) : (
            <DetailsCard className="p-4 max-w-full min-w-0 box-border !rounded-t-none !border-t-0">
              <DocumentLinksSection
                links={reportLinks}
                canManageLinks={canManageLinks}
                isAddingLink={isAddingLink}
                isDeletingLink={isDeletingLink}
                onAddLink={onAddLink}
                onDeleteLink={onDeleteLink}
              />
            </DetailsCard>
          )}
        </div>
      </div>
    </div>
  )
}
